import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  DocumentTypeState,
  EProductionTypes, ProductionOrder, ProductionOrderPlanner, ProductionOrderPlannerOperation, TId, Worker
} from '../../../../../../models';
import {firstValueFrom, take, takeUntil} from 'rxjs';
import { MasterApiService } from '../../../../../../services/master-api.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../../../../services/alert.service';
import DataSource from 'devextreme/data/data_source';
import {
  OmsManufacturingOrdersEditComponent
} from "../../../../../oms/production/oms-manufacturing-orders/oms-manufacturing-orders-edit/oms-manufacturing-orders-edit.component";
import {TabRole, TabsService} from "../../../../../../libs/components/tabs-host/tabs.service";
import {FormBuilder, FormGroup} from "@angular/forms";
import {fromTimeSpanStringToSeconds, fromSecondsToTimeSpanString} from 'src/app/libs/utils'
import {GlamResource} from "../../../../../../libs/directives/glam-resource.directive";

interface PlannerWorker extends Worker {
  isFree?: boolean
}

interface OperationTarjet extends ProductionOrderPlannerOperation {
  workersList?: PlannerWorker[];
  productionOrderStatus?: string;
  color?: string;
  number?: string;
  productionOrderNumber?: string;
  subject?: string;
  documentTypeId?: TId;
  documentTypeStateId?: TId;
}

@Component({
    selector: 'app-edit-workers-planner',
    templateUrl: './edit-workers-planner.component.html',
    styleUrl: './edit-workers-planner.component.sass',
    standalone: false
})
export class EditWorkersPlannerComponent implements OnInit {

  @Input() operation: OperationTarjet | undefined = undefined;
  @Input() planner: ProductionOrderPlanner[] = [];
  @Input() workersOfSelectedOperation: TId[] = [];
  @Output() saveChanges = new EventEmitter<{workers: TId[], stateid: TId, documentId: TId, times:{startTime: Date; endTime: Date} | null}>();
  @Output() move = new EventEmitter();
  @Output() navigate = new EventEmitter<{ productionOrderId: TId }>();
  workersStore?: GlamResource<PlannerWorker>;
  operationDatesForm?: FormGroup;
  documentTypeStates: DocumentTypeState[] = [];
  documentTypeState: TId | undefined = undefined;
  productionOrderResource = this.api.resources.operations.productionOrders();
  productionOrderEditComponent = OmsManufacturingOrdersEditComponent;

constructor(private api: MasterApiService, private tr: TranslateService,private alertService: AlertService, private tab: TabsService, private fb: FormBuilder ) {}

  async ngOnInit(): Promise<void> {
  const plannedOperation = this.planner.find(p => p.id === this.operation?.productionOrderId)
    this.documentTypeStates = await firstValueFrom(this.api.resources.operations.documentTypeStatesByDocumentId(this.operation!.documentTypeId!).getAll());
    this.documentTypeStates = this.documentTypeStates.sort((a, b) => (a.order > b.order) ? 1 : ((a.order < b.order) ? -1 : 0));
    this.documentTypeStates = this.documentTypeStates.filter(o => o.natureStateId === plannedOperation?.natureStateId)
    this.documentTypeState = this.operation!.documentTypeStateId
    const currentOperationStart = this.operation?.startTime;
    const currentOperationEnd = this.operation?.endTime;

    if (currentOperationStart && currentOperationEnd) {
      this.workersStore = this.api.resources.workers.asMappedResource({from: async (worker: PlannerWorker) => {
        const isWorkerBusy = this.planner.some(plannedOperation => {
          return plannedOperation.operations.some((operation: ProductionOrderPlannerOperation) => {
            if(operation.id === this.operation?.id) return false;
            const isWorkerAssigned = operation.workers.includes(worker.id.toString());
            const isOverlapping =
              operation.startTime <= currentOperationEnd &&
              operation.endTime >= currentOperationStart;
            return isWorkerAssigned && isOverlapping;
          });
        });
        worker.isFree = !isWorkerBusy;
        return worker;
      }})
    }
    this.initOperationTimes();
  }

  initOperationTimes(){
    this.operationDatesForm = this.fb.group({
      startTime: [this.operation?.startTime],
      endTime: [this.operation?.endTime],
      duration: [this.operation?.duration],
      productionOrderId: [this.operation?.productionOrderId]
    });
    this.operationDatesForm.controls['productionOrderId'].disable();
    this.listenOperationChanges();
  }

  async onOperationClick() {
    this.navigate.emit( { productionOrderId: this.operation!.productionOrderId })
  }

  save() {

    let times: { startTime: Date; endTime: Date } | null = null;

    if (this.operationDatesForm?.controls['startTime'].value !== this.operation?.startTime ||
      this.operationDatesForm?.controls['endTime'].value !== this.operation?.endTime) {
      times = {
        startTime: this.operationDatesForm?.controls['startTime'].value,
        endTime: this.operationDatesForm?.controls['endTime'].value
      };
    }
    this.saveChanges.emit({ workers: this.workersOfSelectedOperation, stateid: this.documentTypeState! , documentId: this.operation!.productionOrderId, times});
  }

  async moveTicket() {
    const message = this.tr.instant('common.moveConfirmation');
    const result = await this.alertService.confirm(message);
    if(result === false) {
      return;
    }
    this.move.emit();
  }
  private listenOperationChanges(): void {
    const startCtrl = this.operationDatesForm!.get('startTime');
    const endCtrl = this.operationDatesForm!.get('endTime');
    const durationCtrl = this.operationDatesForm!.get('duration');
    const DURATION_REGEX = /^(?=.*\d)(\d+h)?\s*(\d+m)?\s*(\d+s)?$/i;

    durationCtrl?.valueChanges.subscribe(durationString => {
      const start = startCtrl?.value;
      if (!start || !durationString) return;

      const value = durationString.trim();

      if (!DURATION_REGEX.test(value)) return;

      const totalSeconds = fromTimeSpanStringToSeconds(value);

      const newEnd = new Date(start);
      newEnd.setSeconds(newEnd.getSeconds() + totalSeconds);

      endCtrl?.patchValue(newEnd, { emitEvent: false });
    });

    endCtrl?.valueChanges.subscribe(end => {
      const start = startCtrl?.value;
      if (!start || !end) return;

      const diffMs = new Date(end).getTime() - new Date(start).getTime();
      const totalSeconds = Math.floor(diffMs / 1000);

      const durationString = fromSecondsToTimeSpanString(totalSeconds);

      durationCtrl?.patchValue(durationString, { emitEvent: false });
    });

    startCtrl?.valueChanges.subscribe(newStart => {
      const durationString = durationCtrl?.value;
      if (!newStart || !durationString) return;

      const totalSeconds = fromTimeSpanStringToSeconds(durationString);

      const newEnd = new Date(newStart);
      newEnd.setSeconds(newEnd.getSeconds() + totalSeconds);

      endCtrl?.patchValue(newEnd, { emitEvent: false });
    });
  }

}
